'use client';
import React, { useEffect, useMemo, useRef } from 'react';

import sanitizeHtml from 'sanitize-html';

import { Log } from 'helpers/errorLogger';

export interface Props {
  markdown: string;
  className?: string;
  onCollectionLinkClick?: () => void; // Add a prop for the click handler
}

// Broad, XSS-safe allowlist for CMS rich text. `sanitize-html` is pure JS (htmlparser2 — no
// jsdom), so it sanitizes identically on the server and the client: the sanitized HTML is emitted
// in the SSR output (crawlable) and matches on hydration, with NO jsdom dependency.
//
// (Previously this used `isomorphic-dompurify`, which loads `jsdom` on the server. jsdom's
// transitive dep `html-encoding-sniffer` → `@exodus/bytes` is an ES module; `require()`-ing it in
// Netlify's serverless runtime threw ERR_REQUIRE_ESM, which aborted SSR and blanked every page —
// Markdown is rendered in the header, so it affected the whole site.)
const SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: sanitizeHtml.defaults.allowedTags.concat([
    'img',
    'h1',
    'h2',
    'span',
    'figure',
    'figcaption',
    'picture',
    'source',
    'video',
    'u',
    's',
    'mark',
    'small',
    'section',
    'article',
    'header',
    'footer',
    'aside',
    'nav',
  ]),
  allowedAttributes: {
    '*': ['class', 'style', 'id', 'title', 'dir', 'lang', 'data-*', 'aria-*', 'role'],
    a: ['href', 'target', 'rel', 'name'],
    img: ['src', 'alt', 'width', 'height', 'loading', 'srcset', 'sizes'],
    video: ['src', 'controls', 'width', 'height', 'poster'],
    source: ['src', 'srcset', 'type', 'media'],
    td: ['colspan', 'rowspan'],
    th: ['colspan', 'rowspan', 'scope'],
  },
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  allowProtocolRelative: true, // keep protocol-relative + in-page (#collection) anchors
};

const Markdown: React.FC<Props> = ({ markdown, className, onCollectionLinkClick }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Sanitize synchronously (server + client) so the sanitized HTML is in the SSR output.
  const safeMarkdown = useMemo(
    () => (typeof markdown === 'string' ? sanitizeHtml(markdown, SANITIZE_OPTIONS) : ''),
    [markdown],
  );

  useEffect(() => {
    if (containerRef.current) {
      const links = containerRef.current.querySelectorAll('a');

      links.forEach((link) => {
        if (link.href.includes('#collection')) {
          link.addEventListener('click', (event) => {
            event.preventDefault(); // Prevent the default link behavior

            if (onCollectionLinkClick) {
              onCollectionLinkClick(); // Call the provided click handler
            }
          });
        }
      });
    }
  }, [safeMarkdown, onCollectionLinkClick]);

  if (typeof markdown !== 'string') {
    Log.error(new Error(`Markdown: Invalid markdown property. Expected string but received ${typeof markdown}`));

    return <></>;
  }

  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: safeMarkdown }} className={className}></div>;
};

export default Markdown;
